/**
 * The navigation model: which sections exist and which roles may see them.
 *
 * Deliberately free of imports. The role filter is the part worth testing, and
 * keeping it here means a test can import it without pulling in api.ts, which
 * reads `window` at module load and cannot be imported under Node.
 *
 * Gating follows ROLE_PERMISSIONS in src/middleware/rbac.ts, not an assumption
 * about what each role probably does:
 *
 *   - everyone   dashboard, extinguishers, locations, scan, settings, reports.
 *                An inspector holds REPORTS_VIEW, and the reports page reads
 *                only /api/compliance, /api/extinguishers and /api/inspections,
 *                all readable by an inspector. Only the PDF downloads require
 *                REPORTS_GENERATE, and the page guards those separately.
 *   - manager    also team. A manager holds USERS_VIEW ("can view team
 *                members") but not USERS_INVITE; the team page already hides
 *                the invite and remove controls. Hiding the link denied them a
 *                page they are entitled to see.
 *   - admin      also plan. Billing is admin-only.
 */

export interface NavItem {
  label: string;
  path: string;
  /** Roles allowed to see it. Omit for everyone. */
  roles?: string[];
}

// Labels are plain text on purpose. Emoji icons rendered as empty boxes on
// some systems, differ per platform, and pollute the accessible name — a
// screen reader announced "bar chart Dashboard".
export const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard' },
  { label: 'Extinguishers', path: '/extinguishers' },
  { label: 'Locations', path: '/locations' },
  { label: 'Scan', path: '/scan' },
  { label: 'Reports', path: '/reports' },
  { label: 'Team', path: '/team', roles: ['admin', 'manager'] },
  { label: 'Plan', path: '/billing', roles: ['admin'] },
  { label: 'Settings', path: '/settings' },
];

/** Role arrives from a decoded token, so normalise before comparing. */
export function visibleNavItems(role: string | null | undefined): NavItem[] {
  const r = (role ?? '').trim().toLowerCase();
  return NAV_ITEMS.filter((item) => !item.roles || item.roles.includes(r));
}
